import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionsAPI } from '../../api/subscriptions';
import { formatCurrency, formatDateTime, formatPhone } from '../../utils/formatters';
import { NotificationContext } from '../../context/NotificationContext';
import Button from '../common/Button';
import Modal from '../common/Modal';
import PurchaseSubscriptionForm from './PurchaseSubscriptionForm';
import './SubscriptionManagement.css';

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'all',       label: 'All Subscriptions', icon: '📋', status: null },
  { id: 'active',    label: 'Active',            icon: '✅', status: 'active' },
  { id: 'expiring',  label: 'Expiring Soon',     icon: '⏰', status: 'expiring' },
  { id: 'expired',   label: 'Expired',           icon: '⏸️', status: 'expired' },
  { id: 'cancelled', label: 'Cancelled',         icon: '❌', status: 'cancelled' },
];

const STATUS_META = {
  active:    { label: 'Active',    bg: '#d1fae5', color: '#065f46' },
  expiring:  { label: 'Expiring',  bg: '#fef3c7', color: '#92400e' },
  expired:   { label: 'Expired',   bg: '#fee2e2', color: '#991b1b' },
  cancelled: { label: 'Cancelled', bg: '#f3f4f6', color: '#6b7280' },
};

// ─── Inline components ────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, bg: '#f3f4f6', color: '#374151' };
  return <span className="om-badge" style={{ background: m.bg, color: m.color }}>{m.label}</span>;
};

const SummaryCard = ({ icon, label, value, sub, accent }) => (
  <div className="ps-summary-card" style={{ borderTopColor: accent }}>
    <div className="ps-summary-icon">{icon}</div>
    <div>
      <div className="ps-summary-value" style={{ color: accent }}>{value}</div>
      <div className="ps-summary-label">{label}</div>
      {sub && <div className="ps-summary-sub">{sub}</div>}
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);

  // Tab + list state
  const [activeTab, setActiveTab]           = useState('all');
  const [subscriptions, setSubscriptions]   = useState([]);
  const [totalCount, setTotalCount]         = useState(0);
  const [page, setPage]                     = useState(1);
  const [pages, setPages]                   = useState(1);
  const [loading, setLoading]               = useState(false);

  // Filters
  const [search, setSearch]                 = useState('');
  const [filterPlan, setFilterPlan]         = useState('');
  const [filterStart, setFilterStart]       = useState('');
  const [filterEnd, setFilterEnd]           = useState('');

  // Summary
  const [summary, setSummary]               = useState(null);
  const [plans, setPlans]                   = useState([]);

  // Detail modal
  const [selectedSub, setSelectedSub]       = useState(null);
  const [detailLoading, setDetailLoading]   = useState(false);

  // Purchase modal
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseLoading, setPurchaseLoading]     = useState(false);

  // Cancel modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTarget, setCancelTarget]       = useState(null);
  const [cancelReason, setCancelReason]       = useState('');
  const [cancelLoading, setCancelLoading]     = useState(false);

  // ── Load summary ────────────────────────────────────────────────────────────
  const loadSummary = useCallback(async () => {
    try {
      const res = await subscriptionsAPI.getDashboardSummary();
      setSummary(res.data);
    } catch { /* non-critical */ }
  }, []);

  // ── Load plans ──────────────────────────────────────────────────────────────
  const loadPlans = useCallback(async () => {
    try {
      const res = await subscriptionsAPI.getPlans({ isActive: true });
      setPlans(res.data);
    } catch { /* non-critical */ }
  }, []);

  // ── Load subscriptions ──────────────────────────────────────────────────────
  const loadSubscriptions = useCallback(async (resetPage = false) => {
    setLoading(true);
    const p = resetPage ? 1 : page;
    if (resetPage) setPage(1);
    try {
      const params = {
        page: p,
        limit: 20,
        search: search || undefined,
        planId: filterPlan || undefined,
        startDate: filterStart || undefined,
        endDate: filterEnd || undefined,
      };

      const tabStatus = TABS.find(t => t.id === activeTab)?.status;
      if (tabStatus) {
        if (tabStatus === 'expiring') params.expiringSoon = 'true';
        else params.status = tabStatus;
      }

      const res = await subscriptionsAPI.getAll(params);
      setSubscriptions(res.data);
      setTotalCount(res.pagination.total);
      setPages(res.pagination.pages);
    } catch {
      showNotification('Failed to load subscriptions', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, search, filterPlan, filterStart, filterEnd]);

  useEffect(() => { loadSummary(); loadPlans(); }, []);
  useEffect(() => { loadSubscriptions(true); }, [activeTab, search, filterPlan, filterStart, filterEnd]);
  useEffect(() => { if (page > 0) loadSubscriptions(); }, [page]);

  // ── Open detail ─────────────────────────────────────────────────────────────
  const openDetail = async (sub) => {
    setSelectedSub(sub);
    setDetailLoading(true);
    try {
      const res = await subscriptionsAPI.getById(sub._id);
      setSelectedSub(res.data);
    } catch {
      showNotification('Failed to load subscription details', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Purchase subscription ───────────────────────────────────────────────────
  const handlePurchase = async (data) => {
    setPurchaseLoading(true);
    try {
      await subscriptionsAPI.purchase(data);
      showNotification('Subscription purchased successfully', 'success');
      setShowPurchaseModal(false);
      loadSubscriptions();
      loadSummary();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to purchase subscription', 'error');
    } finally {
      setPurchaseLoading(false);
    }
  };

  // ── Cancel subscription ─────────────────────────────────────────────────────
  const openCancelModal = (sub, e) => {
    e?.stopPropagation();
    setCancelTarget(sub);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      showNotification('Please provide a cancellation reason', 'error');
      return;
    }
    setCancelLoading(true);
    try {
      await subscriptionsAPI.cancel(cancelTarget._id, { reason: cancelReason });
      showNotification('Subscription cancelled', 'success');
      setShowCancelModal(false);
      setCancelTarget(null);
      setCancelReason('');
      if (selectedSub?._id === cancelTarget._id) setSelectedSub(null);
      loadSubscriptions();
      loadSummary();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to cancel subscription', 'error');
    } finally {
      setCancelLoading(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const tabCount = (statusKey) => {
    if (!summary || !statusKey) return null;
    if (statusKey === 'active') return summary.activeCount;
    if (statusKey === 'expiring') return summary.expiringSoonCount;
    if (statusKey === 'expired') return summary.expiredCount;
    if (statusKey === 'cancelled') return summary.cancelledCount;
    return null;
  };

  const getDaysRemaining = (endDate) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const clearFilters = () => {
    setFilterPlan(''); setFilterStart(''); setFilterEnd('');
  };

  const hasFilters = filterPlan || filterStart || filterEnd;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="ps-root">

      {/* ── Header ── */}
      <div className="ps-header">
        <div>
          <h1 className="ps-title">Subscription Management</h1>
          <p className="ps-subtitle">Manage customer subscriptions and active plans</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => navigate('/subscriptions/plans')}>
            Manage Plans
          </Button>
          <Button variant="primary" onClick={() => setShowPurchaseModal(true)}>
            + New Subscription
          </Button>
        </div>
      </div>

      {/* ── Summary Cards ── */}
      {summary && (
        <div className="ps-summary-row">
          <SummaryCard
            icon="📋" label="Total Subscriptions" accent="#3b82f6"
            value={summary.totalCount ?? 0}
            sub={`${summary.activeCount ?? 0} active`}
          />
          <SummaryCard
            icon="💰" label="Monthly Revenue" accent="#10b981"
            value={formatCurrency(summary.totalMonthlyRevenue ?? 0)}
            sub="from active subscriptions"
          />
          <SummaryCard
            icon="⏰" label="Expiring Soon" accent="#f59e0b"
            value={summary.expiringSoonCount ?? 0}
            sub="within 7 days"
          />
          <SummaryCard
            icon="👥" label="Active Customers" accent="#8b5cf6"
            value={summary.activeCustomersCount ?? 0}
            sub="with subscriptions"
          />
          <SummaryCard
            icon="📈" label="Avg. Duration" accent="#06b6d4"
            value={`${summary.avgDuration ?? 0} days`}
            sub="per subscription"
          />
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="ps-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`ps-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => { setActiveTab(tab.id); setPage(1); }}
          >
            <span className="ps-tab-icon">{tab.icon}</span>
            {tab.label}
            {tab.status && tabCount(tab.status) != null && (
              <span className="om-tab-count">{tabCount(tab.status)}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="ps-content">
        <div className="ps-tab-pane">

          {/* Filters */}
          <div className="ps-filters">
            <div className="ps-search-wrap">
              <span className="ps-search-icon">🔍</span>
              <input
                className="ps-search-input"
                placeholder="Search by customer name or phone…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="om-search-clear" onClick={() => setSearch('')}>✕</button>
              )}
            </div>
            <select className="ps-filter-select" value={filterPlan} onChange={e => setFilterPlan(e.target.value)}>
              <option value="">All Plans</option>
              {plans.map(p => (
                <option key={p._id} value={p._id}>{p.name}</option>
              ))}
            </select>
            <input type="date" className="ps-filter-date" value={filterStart} onChange={e => setFilterStart(e.target.value)} placeholder="Start Date" />
            <span style={{ color: '#94a3b8', fontSize: 13 }}>→</span>
            <input type="date" className="ps-filter-date" value={filterEnd} onChange={e => setFilterEnd(e.target.value)} placeholder="End Date" />
            {hasFilters && (
              <button className="om-clear-filters" onClick={clearFilters}>Clear filters ✕</button>
            )}
            <Button variant="secondary" onClick={() => { loadSubscriptions(); loadSummary(); }}>↺</Button>
          </div>

          {/* Table meta */}
          <div className="ps-table-meta">
            <span className="ps-count">{totalCount} subscription{totalCount !== 1 ? 's' : ''}</span>
            {summary && activeTab === 'active' && (
              <span>
                Monthly revenue: <strong style={{ color: '#10b981' }}>{formatCurrency(summary.totalMonthlyRevenue ?? 0)}</strong>
              </span>
            )}
          </div>

          {/* Table */}
          {loading ? (
            <div className="ps-loading">Loading subscriptions…</div>
          ) : (
            <>
              <div className="ps-table-wrap">
                <table className="ps-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Plan</th>
                      <th>Duration</th>
                      <th>Price</th>
                      <th>Status</th>
                      <th>Benefits Used</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="ps-empty">
                          <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
                          No subscriptions found
                        </td>
                      </tr>
                    ) : subscriptions.map(sub => {
                      const daysLeft = getDaysRemaining(sub.endDate);
                      const isExpiringSoon = sub.status === 'active' && daysLeft <= 7;

                      return (
                        <tr key={sub._id} className="ps-row" onClick={() => openDetail(sub)}>
                          {/* Customer */}
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div className="om-avatar-sm">
                                {sub.customer?.name?.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="om-cust-name-cell">{sub.customer?.name}</div>
                                <div className="om-cust-phone-cell">{formatPhone(sub.customer?.phone)}</div>
                              </div>
                            </div>
                          </td>

                          {/* Plan */}
                          <td>
                            <div style={{ fontWeight: 700, color: '#6366f1', fontSize: 13 }}>
                              {sub.subscriptionPlan?.name}
                            </div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>
                              {sub.subscriptionPlan?.duration} days
                            </div>
                          </td>

                          {/* Duration */}
                          <td style={{ fontSize: 13, color: '#64748b' }}>
                            {sub.subscriptionPlan?.duration} days
                          </td>

                          {/* Price */}
                          <td style={{ fontWeight: 800, color: '#0f172a' }}>
                            {formatCurrency(sub.subscriptionPlan?.price)}
                          </td>

                          {/* Status */}
                          <td>
                            <StatusBadge status={sub.status} />
                            {isExpiringSoon && (
                              <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 2, fontWeight: 600 }}>
                                ⏰ {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
                              </div>
                            )}
                          </td>

                          {/* Benefits Used */}
                          <td>
                            <div style={{ fontSize: 12 }}>
                              {sub.subscriptionPlan?.benefits?.bundleClothes > 0 && (
                                <div style={{ marginBottom: 2 }}>
                                  👔 {sub.remainingBundleClothes ?? 0} / {sub.subscriptionPlan.benefits.bundleClothes}
                                </div>
                              )}
                              {sub.subscriptionPlan?.benefits?.freeItems > 0 && (
                                <div style={{ marginBottom: 2 }}>
                                  🎁 {sub.remainingFreeItems ?? 0} / {sub.subscriptionPlan.benefits.freeItems}
                                </div>
                              )}
                              {!sub.subscriptionPlan?.benefits?.bundleClothes && !sub.subscriptionPlan?.benefits?.freeItems && (
                                <span style={{ color: '#94a3b8' }}>—</span>
                              )}
                            </div>
                          </td>

                          {/* Start Date */}
                          <td className="ps-date">{formatDateTime(sub.startDate).split(',')[0]}</td>

                          {/* End Date */}
                          <td className="ps-date">{formatDateTime(sub.endDate).split(',')[0]}</td>

                          {/* Actions */}
                          <td onClick={e => e.stopPropagation()}>
                            {sub.status === 'active' && (
                              <button
                                className="om-action-btn danger"
                                onClick={e => openCancelModal(sub, e)}
                                title="Cancel subscription"
                              >
                                ❌
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {pages > 1 && (
                <div className="ps-pagination">
                  <Button variant="secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</Button>
                  <span className="ps-page-label">Page {page} of {pages}</span>
                  <Button variant="secondary" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          SUBSCRIPTION DETAIL MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      {selectedSub && (
        <Modal
          isOpen={!!selectedSub}
          onClose={() => setSelectedSub(null)}
          title={`Subscription - ${selectedSub.customer?.name}`}
          size="large"
        >
          {detailLoading ? (
            <div className="ps-loading">Loading details…</div>
          ) : (
            <SubscriptionDetailContent
              subscription={selectedSub}
              onCancel={openCancelModal}
              formatCurrency={formatCurrency}
              formatDateTime={formatDateTime}
              formatPhone={formatPhone}
              getDaysRemaining={getDaysRemaining}
            />
          )}
        </Modal>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PURCHASE MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        title="Purchase New Subscription"
        size="medium"
      >
        <PurchaseSubscriptionForm
          plans={plans}
          onSubmit={handlePurchase}
          onCancel={() => setShowPurchaseModal(false)}
        />
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════════
          CANCEL MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => { setShowCancelModal(false); setCancelTarget(null); }}
        title="Cancel Subscription"
      >
        {cancelTarget && (
          <div className="ps-modal-form">
            <div className="om-delete-warning">
              <span style={{ fontSize: 32 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>
                  Cancel subscription for {cancelTarget.customer?.name}?
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  Plan: <strong>{cancelTarget.subscriptionPlan?.name}</strong><br />
                  This will immediately deactivate the subscription and prevent further benefits.
                </div>
              </div>
            </div>
            <div className="ps-modal-field">
              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 6, display: 'block' }}>
                Cancellation Reason *
              </label>
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                rows={3}
                style={{
                  width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 8,
                  padding: '9px 13px', fontSize: 14, fontFamily: "'DM Sans', sans-serif",
                  resize: 'vertical', lineHeight: 1.5
                }}
                placeholder="Enter reason for cancellation…"
              />
            </div>
            <div className="ps-modal-actions">
              <Button variant="secondary" onClick={() => { setShowCancelModal(false); setCancelTarget(null); }}>
                Keep Active
              </Button>
              <Button variant="danger" onClick={handleCancel} disabled={cancelLoading}>
                {cancelLoading ? 'Cancelling…' : 'Cancel Subscription'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ─── Subscription Detail Content ──────────────────────────────────────────────
const SubscriptionDetailContent = ({ subscription, onCancel, formatCurrency, formatDateTime, formatPhone, getDaysRemaining }) => {
  const daysLeft = getDaysRemaining(subscription.endDate);
  const plan = subscription.subscriptionPlan;
  const benefits = plan?.benefits || {};

  return (
    <div className="sm-detail">

      {/* Top bar */}
      <div className="om-detail-top-bar">
        <div className="om-detail-badges">
          <StatusBadge status={subscription.status} />
          {subscription.status === 'active' && daysLeft <= 7 && (
            <span className="om-badge" style={{ background: '#fef3c7', color: '#92400e' }}>
              ⏰ {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
            </span>
          )}
          {benefits.discountPercentage > 0 && (
            <span className="om-badge" style={{ background: '#d1fae5', color: '#065f46' }}>
              💸 {benefits.discountPercentage}% OFF
            </span>
          )}
          {benefits.expressDeliveryFree && (
            <span className="om-badge" style={{ background: '#dbeafe', color: '#1e40af' }}>
              🚀 Free Express
            </span>
          )}
        </div>
        {subscription.status === 'active' && (
          <button className="ps-settle-btn-lg" style={{ background: '#ef4444' }} onClick={(e) => onCancel(subscription, e)}>
            Cancel Subscription
          </button>
        )}
      </div>

      {/* 3-column grid */}
      <div className="sm-detail-grid">

        {/* Customer Info */}
        <div className="om-detail-section">
          <div className="om-section-title">Customer Information</div>
          <div className="om-customer-card">
            <div className="om-avatar-lg">{subscription.customer?.name?.charAt(0).toUpperCase()}</div>
            <div className="om-customer-info">
              <div className="om-cust-name-lg">{subscription.customer?.name}</div>
              <div className="om-cust-detail">📞 {formatPhone(subscription.customer?.phone)}</div>
              {subscription.customer?.email && (
                <div className="om-cust-detail">✉️ {subscription.customer.email}</div>
              )}
            </div>
          </div>
        </div>

        {/* Plan Details */}
        <div className="om-detail-section">
          <div className="om-section-title">Plan Details</div>
          {[
            ['Plan Name',    plan?.name],
            ['Duration',     `${plan?.duration} days`],
            ['Price',        formatCurrency(plan?.price)],
            ['Start Date',   formatDateTime(subscription.startDate).split(',')[0]],
            ['End Date',     formatDateTime(subscription.endDate).split(',')[0]],
            ['Purchased By', subscription.purchasedBy?.name || '—'],
          ].map(([k, v]) => (
            <div key={k} className="om-info-row">
              <span className="om-info-label">{k}</span>
              <span className="om-info-val">{v}</span>
            </div>
          ))}
        </div>

        {/* Benefits & Usage */}
        <div className="om-detail-section">
          <div className="om-section-title">Benefits & Usage</div>
          <div className="sm-benefits-card">
            {benefits.discountPercentage > 0 && (
              <div className="sm-benefit-row">
                <span className="sm-benefit-icon">💸</span>
                <div>
                  <div className="sm-benefit-label">Discount on Orders</div>
                  <div className="sm-benefit-value">{benefits.discountPercentage}% OFF</div>
                </div>
              </div>
            )}
            {benefits.bundleClothes > 0 && (
              <div className="sm-benefit-row">
                <span className="sm-benefit-icon">👔</span>
                <div>
                  <div className="sm-benefit-label">Bundle Items</div>
                  <div className="sm-benefit-value">
                    {subscription.remainingBundleClothes ?? 0} / {benefits.bundleClothes} remaining
                  </div>
                </div>
              </div>
            )}
            {benefits.freeItems > 0 && (
              <div className="sm-benefit-row">
                <span className="sm-benefit-icon">🎁</span>
                <div>
                  <div className="sm-benefit-label">Free Items</div>
                  <div className="sm-benefit-value">
                    {subscription.remainingFreeItems ?? 0} / {benefits.freeItems} remaining
                  </div>
                </div>
              </div>
            )}
            {benefits.expressDeliveryFree && (
              <div className="sm-benefit-row">
                <span className="sm-benefit-icon">🚀</span>
                <div>
                  <div className="sm-benefit-label">Express Delivery</div>
                  <div className="sm-benefit-value">FREE ✓</div>
                </div>
              </div>
            )}
            {benefits.priorityService && (
              <div className="sm-benefit-row">
                <span className="sm-benefit-icon">⭐</span>
                <div>
                  <div className="sm-benefit-label">Priority Service</div>
                  <div className="sm-benefit-value">Enabled ✓</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notes */}
      {subscription.notes && (
        <div className="om-notes-box">
          <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Notes</span>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#374151' }}>{subscription.notes}</p>
        </div>
      )}

      {/* Cancellation info */}
      {subscription.status === 'cancelled' && subscription.cancellationReason && (
        <div style={{
          background: '#fee2e2', border: '1.5px solid #fca5a5',
          borderRadius: 10, padding: '12px 16px'
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', marginBottom: 4 }}>
            Cancellation Reason
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#7f1d1d' }}>{subscription.cancellationReason}</p>
          {subscription.cancelledAt && (
            <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4 }}>
              Cancelled on {formatDateTime(subscription.cancelledAt)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
